import { computed } from 'vue';
import getAccountAiReports from '@gpx-spa/queries/src/getAccountAiReports';
import { useStore } from 'vuex';
import { getApi } from '@gpx-spa/queries/src/api';

export interface AiReport {
  id: number;
  title: string;
  created_at: string;
  result?: string;
}

export const useInsights = () => {
  const store = useStore();
  const accountId = computed(() => store.getters['auth/accountId']);
  const fiveDayAgo = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  // Fetch insights using the existing query
  const {
    data: insights,
    isLoading,
    error,
    refetch,
  } = getAccountAiReports(accountId.value, { status: 'completed', created_since: fiveDayAgo, is_approved: true });

  // Transform the data to match our component expectations
  const transformedInsights = computed((): AiReport[] => {
    if (!insights.value) return [];
    return insights.value;
  });

  // Get insight by ID
  const getInsightById = (id: number | string): AiReport | undefined => {
    return transformedInsights.value.find(
      (insight) => insight.id === Number(id),
    );
  };

  // Search functionality
  const searchInsights = (query: string): AiReport[] => {
    if (!query.trim()) return transformedInsights.value;

    return transformedInsights.value.filter(
      (insight) =>
        insight.title.toLowerCase().includes(query.toLowerCase()) ||
        insight.result?.toLowerCase().includes(query.toLowerCase()),
    );
  };

  // Download PDF report
  const downloadPdfReport = async (reportId: number | string): Promise<boolean> => {
    try {
      if (!accountId.value || !reportId) {
        console.error('Missing account or report information');
        return false;
      }

      const { api, baseURL } = getApi();

      const response = await api.get(`api/v2/accounts/${accountId.value}/ai-reports/${reportId}/pdf`, {
        baseURL,
        useOriginalToken: true,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `geofence-report-${reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Download error:', error);
      return false;
    }
  };

  return {
    insights: transformedInsights,
    isLoading,
    error,
    refetch,
    getInsightById,
    searchInsights,
    accountId,
    downloadPdfReport,
  };
};
